<script setup lang="ts">
import type { AuthStore } from '@/stores/authStore.types';
import { useRoute } from 'vue-router';
import { useForm } from 'vee-validate';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import InputOtp from 'primevue/inputotp';

import * as yup from '@/helpers/yup-distilled';

import { router, getNextRouteFromNewSigninState } from '@/helpers';
import { getFriendlyErrorMessage } from '@/helpers';
import { useAuthStore } from '@/stores';

const props = defineProps<{
	withPasscode: boolean;
	headlineText: string;
	helpText: string;
}>();
const route = useRoute();
const authStore: AuthStore = useAuthStore();

// redirect to login if no auth token
if (!authStore.authToken && !props.withPasscode) {
	router.push(import.meta.env.VITE_SIGNIN_BASEURL);
}

const passwordRules = {
	length: (val: string) => (val && val.length >= 8) || !val,
	uppercase: (val: string) => (val && /[A-Z]/.test(val)) || !val,
	lowercase: (val: string) => (val && /[a-z]/.test(val)) || !val,
	number: (val: string) => (val && /[0-9]/.test(val)) || !val,
	special: (val: string) => (val && /[!@#$%^&*:]/.test(val)) || !val,
	noOthers: (val: string) =>
		(val && !/[^0-9a-zA-Z!@#$%^&*:]/.test(val)) || !val
};

const schema = yup.object().shape({
	withPasscode: yup.boolean(),
	passcode: yup.string().when('withPasscode', {
		is: true,
		then: () => yup.string().required()
	}),
	newpassword: yup
		.string()
		.required('Please specify a new password.')
		.test(
			'strengthValidity',
			'Please see the password guidelines above.',
			(val) =>
				passwordRules.length(val) &&
				passwordRules.uppercase(val) &&
				passwordRules.lowercase(val) &&
				passwordRules.number(val) &&
				passwordRules.special(val) &&
				passwordRules.noOthers(val)
		),
	confirmpassword: yup
		.string()
		.oneOf(
			[yup.ref('newpassword'), null as unknown as string],
			'Passwords must match.'
		)
});

const { defineField, errors, handleSubmit, isSubmitting } = useForm({
	validationSchema: schema,
	initialValues: {
		withPasscode: props.withPasscode,
		passcode: '',
		newpassword: '',
		confirmpassword: '',
		apiError: ''
	}
});

const getPasswordResetPromise = (
	newpassword: string,
	confirmpassword: string,
	passcode: string
) => {
	if (props.withPasscode) {
		return authStore.resetPassword(newpassword, confirmpassword, passcode);
	} else {
		return authStore.resetPassword(newpassword, confirmpassword);
	}
};

const getPasswordRuleClass = (
	newpassword: string,
	rule: (val: string) => boolean
) => {
	if (!newpassword) return '';
	return rule(newpassword) ? 'text-green-500' : 'text-red-500';
};

const onSubmit = handleSubmit((values, { setErrors }) => {
	const { newpassword, confirmpassword, passcode } = values;

	return getPasswordResetPromise(newpassword, confirmpassword, passcode)
		.then(() => {
			router.replace(
				getNextRouteFromNewSigninState(authStore.signInState, route)
			);
		})
		.catch(async (error: Error) => {
			const errorOutput = await getFriendlyErrorMessage(error);
			setErrors({ apiError: errorOutput.message });
		});
});

const [passcode] = defineField('passcode');
const [newpassword] = defineField('newpassword');
const [confirmpassword] = defineField('confirmpassword');
</script>

<template>
	<form @submit="onSubmit" class="reset-password">
		<div class="p-fluid login-large-form">
			<div v-if="props.headlineText" class="p-field font-bold">
				{{ props.headlineText }}
			</div>
			<div v-if="props.helpText" class="p-field">
				{{ props.helpText }}
			</div>
			<div class="p-field mt-2">The new password must contain:</div>
			<ul class="tight-ul">
				<li
					:class="
						getPasswordRuleClass(newpassword, passwordRules.length)
					"
				>
					At least 8 total characters
				</li>
				<li
					:class="
						getPasswordRuleClass(
							newpassword,
							passwordRules.uppercase
						)
					"
				>
					At least one uppercase letter
				</li>
				<li
					:class="
						getPasswordRuleClass(
							newpassword,
							passwordRules.lowercase
						)
					"
				>
					At least one uppercase letter
				</li>
				<li
					:class="
						getPasswordRuleClass(newpassword, passwordRules.number)
					"
				>
					At least one number
				</li>
				<li
					:class="
						getPasswordRuleClass(newpassword, passwordRules.special)
					"
				>
					At least one special character
					<strong>! @ # $ % ^ & * :</strong>
				</li>
				<li
					:class="
						getPasswordRuleClass(
							newpassword,
							passwordRules.noOthers
						)
					"
				>
					No special character other than the listed ones
				</li>
			</ul>
			<div class="p-field mt-2" v-if="withPasscode">
				<label for="passcode">Please enter the passcode below.</label>
				<InputOtp
					autofocus
					v-model="passcode"
					:invalid="!!errors.passcode"
					:length="8"
					:pt="{
						input: {
							root: {
								style: 'width: 2.5rem; text-align: center'
							}
						}
					}"
				/>
			</div>
			<div class="p-field mt-2">
				<label for="newpassword"
					>Please enter a new password below.</label
				>
				<InputText
					autofocus
					autocomplete="new-password"
					v-model="newpassword"
					:invalid="!!errors.newpassword"
					type="password"
				></InputText>
			</div>
			<div class="p-error">{{ errors.newpassword }}&nbsp;</div>
			<div class="p-field">
				<label for="confirmpassword"
					>To confirm, please re-enter the same password.</label
				>
				<InputText
					autocomplete="confirm-password"
					v-model="confirmpassword"
					:invalid="!!errors.confirmpassword"
					type="password"
				></InputText>
			</div>
			<div class="p-error mb-2">{{ errors.confirmpassword }}&nbsp;</div>
		</div>
		<div class="p-fluid">
			<Button
				:disabled="isSubmitting"
				label="Submit"
				type="submit"
			></Button>
			<div v-if="errors.apiError" class="p-error">
				{{ errors.apiError }}
			</div>
		</div>
	</form>
</template>

<style scoped>
:deep(ul.tight-ul) {
	margin-block-start: 0.5em;
	margin-block-end: 0.5em;
}
</style>
